import { Component, AfterViewInit, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { OrganizationDisplay } from '../../organization-models';
import { CreateChargingUserWithAppInvitationModalComponent } from 'src/app/features/charging-users/charging-user-create-modal/modals/create-charging-user-with-app-invitation.modal/create-charging-user-with-app-invitation.modal.component';
import { CreateChargingUserIdTagWithInvitationModalComponent } from 'src/app/features/charging-users/charging-user-create-modal/modals/create-charging-user-id-tag-with-invitation.modal/create-charging-user-id-tag-with-invitation.modal.component';
import { CreateChargingUserIdTagWithoutInvitationModalComponent } from 'src/app/features/charging-users/charging-user-create-modal/modals/create-charging-user-id-tag-without-invitation.modal/create-charging-user-id-tag-without-invitation.modal.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ChargingUser } from 'src/app/features/charging-users/charging-user.model';
import { ChargingUserAppInvitationsModalComponent } from 'src/app/features/charging-users/app-invitation-list/app-invitations-modal.component';
import { ChargingUserProcessModalComponent } from 'src/app/features/charging-users/charging-user-process-modal/charging-user-process-modal.component';
import { first, switchMap } from 'rxjs/operators';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { ChargingUserAddIdtaginfoModalComponent } from 'src/app/features/charging-users/charging-user-add-id-tag-info-modal/charging-user-add-id-tag-info-modal.component';

export interface CreateUserDialogData {
  organization: OrganizationDisplay;
}

export type UserCreationType = 'app-invitation' | 'idtag-with-invitation' | 'idtag-without-invitation';

@Component({
  selector: 'adm-app-create-user-dialog',
  templateUrl: './create-user-dialog.component.html',
  styleUrl: './create-user-dialog.component.scss',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    CustomDropDownArrowDirective,
  ],
})
export class CreateUserDialogComponent implements AfterViewInit {
  public selectedType = new FormControl<UserCreationType | null>(null);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  public dialogRef = inject(MatDialogRef<CreateUserDialogComponent>);
  public data = inject<CreateUserDialogData>(MAT_DIALOG_DATA);

  ngAfterViewInit() {
    // Subscribe to form control value changes
    this.selectedType.valueChanges.subscribe(value => {
      if (value) {
        this.openSelectedModal(value);
      }
    });
  }

  public processChargingUser(chargingUser: ChargingUser) {
    const dialogRef = this.dialog.open(ChargingUserProcessModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe(closeResult => {
      if (closeResult.openAddIdTagInfoModal) {
        this.openAddIdTagInfoModal(chargingUser);
      }
    });
  }

  public openAddIdTagInfoModal(chargingUser: ChargingUser) {
    const addIdTagInfoModalRef = this.dialog.open(ChargingUserAddIdtaginfoModalComponent, {
      data: {
        chargingUser: chargingUser,
      },
      disableClose: true,
    });

    addIdTagInfoModalRef
      .afterClosed()
      .pipe(
        switchMap(_ => this.chargingUserService.getChargingUser(chargingUser.id)),
        first()
      )
      .subscribe({
        next: (updatedChargingUser: ChargingUser) => {
          this.processChargingUser(updatedChargingUser);
        },
        error: () => {
          this.snackbarService.error('Der skete en fejl under opdatering af brikinformation');
          this.processChargingUser(chargingUser);
        },
      });
  }

  private openSelectedModal(userType: UserCreationType) {
    // Close current dialog
    this.dialogRef.close();

    // Open the specific modal based on selection
    switch (userType) {
      case 'app-invitation': {
        const refAppInvitation = this.dialog.open(CreateChargingUserWithAppInvitationModalComponent, {
          data: {
            paymentTerminal: null,
            organization: this.data.organization,
          },
        });

        refAppInvitation.afterClosed().subscribe((user: ChargingUser) => {
          if (user) {
            this.dialog.open(ChargingUserAppInvitationsModalComponent, {
              data: {
                chargingUser: user,
              },
              width: '1280px',
            });
          }
        });
        break;
      }
      case 'idtag-with-invitation': {
        const refWithInvitation = this.dialog.open(CreateChargingUserIdTagWithInvitationModalComponent, {
          data: {
            paymentTerminal: null,
            organization: this.data.organization,
          },
        });

        refWithInvitation.afterClosed().subscribe((user: ChargingUser) => {
          if (user) {
            this.processChargingUser(user);
          }
        });
        break;
      }
      case 'idtag-without-invitation': {
        const refWithoutInvitation = this.dialog.open(CreateChargingUserIdTagWithoutInvitationModalComponent, {
          data: {
            paymentTerminal: null,
            organization: this.data.organization,
          },
        });

        refWithoutInvitation.afterClosed().subscribe((user: ChargingUser) => {
          if (user) {
            this.processChargingUser(user);
          }
        });
        break;
      }
    }
  }
}
